import React, { useEffect, useState } from "react";
import yaml from "yaml";
import styles from "./PolicyEditor.module.css";
import { InfoTip } from "./InfoTip";
import MarkdownPanel from "../ChatGPT/MarkdownPanel";
// ── EndpointProfileEditor ──────────────────────────────────────────────
export const EndpointProfileEditor = ({ endpoint, onChange, onRemove }) => {
    const update = (partial) => onChange({ ...endpoint, ...partial });
    const hasRules = Array.isArray(endpoint.rules) && endpoint.rules.length > 0;
    const useAccess = !!endpoint.access && !hasRules;
    return (React.createElement("div", { className: styles.endpointCard },
        React.createElement("div", { className: styles.endpointHeader },
            React.createElement("span", { className: styles.endpointLabel },
                endpoint.host || "new endpoint",
                ":",
                endpoint.port ?? 443),
            React.createElement("button", { className: styles.removeBtn, onClick: onRemove, title: "Remove endpoint" }, "\u00D7")),
        React.createElement("div", { className: styles.fieldRow },
            React.createElement("div", { className: styles.field },
                React.createElement("div", { className: styles.labelWithInfo },
                    React.createElement("label", { className: styles.label }, "Host"),
                    React.createElement(InfoTip, { title: "Host" }, "The domain name or IP address the agent needs to reach (e.g. api.github.com). You can use wildcards like *.example.com to allow all subdomains.")),
                React.createElement("input", { className: styles.inputSmall, value: endpoint.host, onChange: (e) => update({ host: e.target.value }), placeholder: "api.example.com" })),
            React.createElement("div", { className: styles.field },
                React.createElement("div", { className: styles.labelWithInfo },
                    React.createElement("label", { className: styles.label }, "Port"),
                    React.createElement(InfoTip, { title: "Port" }, "The network port to allow. 443 is standard for HTTPS (secure web traffic). 80 is plain HTTP. Most APIs use 443.")),
                React.createElement("input", { className: styles.inputSmall, type: "number", value: endpoint.port ?? 443, onChange: (e) => update({ port: parseInt(e.target.value) || 443 }) }))),
        React.createElement("div", { className: styles.fieldRow3 },
            React.createElement("div", { className: styles.field },
                React.createElement("div", { className: styles.labelWithInfo },
                    React.createElement("label", { className: styles.label }, "Protocol"),
                    React.createElement(InfoTip, { title: "Protocol" },
                        React.createElement("strong", null, "TCP passthrough"),
                        " \u2014 Allows the connection without inspecting what's sent. Simpler but less control.",
                        React.createElement("br", null),
                        React.createElement("br", null),
                        React.createElement("strong", null, "REST / GraphQL (HTTP inspect)"),
                        " \u2014 Inspects each HTTP request, allowing you to control which API methods and paths are permitted. More secure for REST APIs.")),
                React.createElement("select", { className: styles.select, value: endpoint.protocol ?? "", onChange: (e) => update({ protocol: (e.target.value || undefined) }) },
                    React.createElement("option", { value: "" }, "TCP passthrough"),
                    React.createElement("option", { value: "rest" }, "rest"),
                    React.createElement("option", { value: "graphql" }, "graphql"),
                    React.createElement("option", { value: "websocket" }, "websocket"),
                    React.createElement("option", { value: "sql" }, "sql"))),
            React.createElement("div", { className: styles.field },
                React.createElement("div", { className: styles.labelWithInfo },
                    React.createElement("label", { className: styles.label }, "TLS"),
                    React.createElement(InfoTip, { title: "TLS Handling" },
                        React.createElement("strong", null, "Terminate"),
                        " \u2014 Decrypts the secure connection at the proxy so it can inspect the content. Required for REST protocol inspection.",
                        React.createElement("br", null),
                        React.createElement("br", null),
                        React.createElement("strong", null, "Passthrough"),
                        " \u2014 Forwards encrypted traffic as-is without inspecting it. Use when you don't need content inspection.")),
                React.createElement("select", { className: styles.select, value: endpoint.tls ?? "", onChange: (e) => update({ tls: (e.target.value || undefined) }) },
                    React.createElement("option", { value: "" }, "Default"),
                    React.createElement("option", { value: "terminate" }, "terminate"),
                    React.createElement("option", { value: "passthrough" }, "passthrough"))),
            React.createElement("div", { className: styles.field },
                React.createElement("div", { className: styles.labelWithInfo },
                    React.createElement("label", { className: styles.label }, "Enforcement"),
                    React.createElement(InfoTip, { title: "Enforcement Mode" },
                        React.createElement("strong", null, "Enforce"),
                        " \u2014 Actively blocks any request that doesn't match the rules. Use in production.",
                        React.createElement("br", null),
                        React.createElement("br", null),
                        React.createElement("strong", null, "Audit"),
                        " \u2014 Logs violations but allows traffic through. Useful for testing new policies before enforcing them.")),
                React.createElement("select", { className: styles.select, value: endpoint.enforcement ?? "", onChange: (e) => update({ enforcement: (e.target.value || undefined) }) },
                    React.createElement("option", { value: "" }, "Default"),
                    React.createElement("option", { value: "enforce" }, "enforce"),
                    React.createElement("option", { value: "audit" }, "audit")))),
        (endpoint.protocol === "rest" || endpoint.protocol === "graphql") && (React.createElement("div", { className: styles.field },
            React.createElement("div", { className: styles.labelWithInfo },
                React.createElement("label", { className: styles.label },
                    "Access mode ",
                    React.createElement("span", { className: styles.labelHint }, "(or use rules below)")),
                React.createElement(InfoTip, { title: "Access Mode" },
                    "A quick way to set permissions without writing individual rules.",
                    React.createElement("br", null),
                    React.createElement("br", null),
                    React.createElement("strong", null, "Full"),
                    " \u2014 All HTTP methods allowed (GET, POST, PUT, DELETE, etc.).",
                    React.createElement("br", null),
                    React.createElement("strong", null, "Read-only"),
                    " \u2014 Only GET, HEAD, OPTIONS. The agent can view data but not change anything.",
                    React.createElement("br", null),
                    React.createElement("strong", null, "Read-write"),
                    " \u2014 GET, HEAD, OPTIONS, POST, PUT, PATCH. No DELETE.",
                    React.createElement("br", null),
                    React.createElement("br", null),
                    "Choose \"Custom rules\" below if you need more specific control over which API paths are accessible.")),
            React.createElement("select", { className: styles.select, value: useAccess ? (endpoint.access ?? "") : "", onChange: (e) => {
                    if (e.target.value) {
                        update({ access: e.target.value, rules: undefined });
                    }
                    else {
                        update({ access: undefined });
                    }
                } },
                React.createElement("option", { value: "" }, "Custom rules"),
                React.createElement("option", { value: "full" }, "full"),
                React.createElement("option", { value: "read-only" }, "read-only"),
                React.createElement("option", { value: "read-write" }, "read-write")))),
        (endpoint.protocol === "rest" || endpoint.protocol === "graphql") && !useAccess && (React.createElement("div", { className: styles.field },
            React.createElement("div", { className: styles.labelWithInfo },
                React.createElement("span", { className: styles.subLabel }, "Rules"),
                React.createElement(InfoTip, { title: "Custom Rules" }, "Fine-grained control over which API calls are allowed. Each rule specifies an HTTP method (GET, POST, etc.) and a URL path pattern. Use ** as a wildcard to match any path segment. For example, GET /** allows reading any endpoint, while POST /repos/*/issues only allows creating issues.")),
            (endpoint.rules ?? []).map((rule, ri) => (React.createElement("div", { key: ri, className: styles.ruleRow },
                React.createElement("select", { className: styles.select, value: rule.allow?.method ?? "GET", onChange: (e) => {
                        const next = [...(endpoint.rules ?? [])];
                        next[ri] = { allow: { ...(rule.allow ?? {}), method: e.target.value } };
                        update({ rules: next });
                    } }, ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS", "*"].map((m) => (React.createElement("option", { key: m, value: m }, m)))),
                React.createElement("input", { className: styles.inputSmall, value: rule.allow?.path ?? "", onChange: (e) => {
                        const next = [...(endpoint.rules ?? [])];
                        next[ri] = { allow: { ...(rule.allow ?? {}), path: e.target.value } };
                        update({ rules: next });
                    }, placeholder: "/**" }),
                React.createElement("button", { className: styles.removeBtn, onClick: () => {
                        const next = (endpoint.rules ?? []).filter((_, j) => j !== ri);
                        update({ rules: next.length ? next : undefined });
                    } }, "\u00D7")))),
            React.createElement("button", { className: styles.addBtn, onClick: () => {
                    const next = [...(endpoint.rules ?? []), { allow: { method: "GET", path: "/**" } }];
                    update({ rules: next });
                } }, "+ Add rule"))),
        React.createElement("div", { className: styles.fieldRow, style: { marginTop: 8 } },
            React.createElement("label", { className: styles.checkbox },
                React.createElement("input", { type: "checkbox", checked: !!endpoint.websocket_credential_rewrite, onChange: (e) => update({ websocket_credential_rewrite: e.target.checked }) }),
                "WS credential rewrite"),
            React.createElement("label", { className: styles.checkbox },
                React.createElement("input", { type: "checkbox", checked: !!endpoint.request_body_credential_rewrite, onChange: (e) => update({ request_body_credential_rewrite: e.target.checked }) }),
                "Request body credential rewrite"))));
};
// ── BinaryProfileList ──────────────────────────────────────────────────
const BinaryProfileList = ({ binaries, onChange }) => (React.createElement("div", { className: styles.pathList },
    binaries.map((b, i) => (React.createElement("div", { key: i, className: styles.pathItem },
        React.createElement("input", { className: styles.inputSmall, value: b.path, onChange: (e) => {
                const next = [...binaries];
                next[i] = { ...b, path: e.target.value };
                onChange(next);
            }, placeholder: "/usr/bin/example" }),
        React.createElement("label", { className: styles.checkbox, style: { marginLeft: 8, whiteSpace: "nowrap" } },
            React.createElement("input", { type: "checkbox", checked: !!b.harness, onChange: (e) => {
                    const next = [...binaries];
                    next[i] = { ...b, harness: e.target.checked };
                    onChange(next);
                } }),
            "harness"),
        React.createElement("button", { className: styles.removeBtn, onClick: () => onChange(binaries.filter((_, j) => j !== i)) }, "\u00D7")))),
    React.createElement("button", { className: styles.addBtn, onClick: () => onChange([...binaries, { path: "" }]) }, "+ Add binary")));
// ── CredentialRefreshMaterialEditor ───────────────────────────────────
const CredentialRefreshMaterialEditor = ({ material, onChange, onRemove }) => {
    const update = (partial) => onChange({ ...material, ...partial });
    return (React.createElement("div", { className: styles.endpointCard },
        React.createElement("div", { className: styles.endpointHeader },
            React.createElement("span", { className: styles.endpointLabel }, material.name || "new material"),
            React.createElement("button", { className: styles.removeBtn, onClick: onRemove, title: "Remove" }, "\u00D7")),
        React.createElement("div", { className: styles.fieldRow },
            React.createElement("div", { className: styles.field },
                React.createElement("label", { className: styles.label }, "Name"),
                React.createElement("input", { className: styles.inputSmall, value: material.name, onChange: (e) => update({ name: e.target.value }), placeholder: "client_id" })),
            React.createElement("div", { className: styles.field },
                React.createElement("label", { className: styles.label }, "Description"),
                React.createElement("input", { className: styles.inputSmall, value: material.description ?? "", onChange: (e) => update({ description: e.target.value || undefined }), placeholder: "Optional description" }))),
        React.createElement("div", { style: { display: "flex", gap: 16, marginTop: 6 } },
            React.createElement("label", { className: styles.checkbox },
                React.createElement("input", { type: "checkbox", checked: !!material.required, onChange: (e) => update({ required: e.target.checked }) }),
                "Required"),
            React.createElement("label", { className: styles.checkbox },
                React.createElement("input", { type: "checkbox", checked: !!material.secret, onChange: (e) => update({ secret: e.target.checked }) }),
                "Secret"))));
};
function getDefaultMaterialForStrategy(strategy) {
    switch (strategy) {
        case "oauth2_refresh_token":
            return [
                {
                    name: "client_id",
                    required: true
                },
                {
                    name: "refresh_token",
                    required: true,
                    secret: true
                },
                {
                    name: "client_secret",
                    required: true,
                    secret: true
                }
            ];
        case "oauth2_client_credentials":
            return [
                {
                    name: "client_id",
                    required: true
                },
                {
                    name: "client_secret",
                    secret: true
                },
                {
                    name: "refresh_token",
                    required: true,
                    secret: true
                }
            ];
        case "google_service_account_jwt":
            return [
                {
                    name: "client_email",
                    required: true
                },
                {
                    name: "private_key",
                    required: true,
                    secret: true
                },
                {
                    name: "subject"
                }
            ];
        case "static":
        case "external":
        default:
            return [];
    }
}
// ── CredentialRefreshEditor ────────────────────────────────────────────
const DEFAULT_REFRESH = {
    strategy: "static",
    token_url: "",
    scopes: [],
    refresh_before_seconds: 0,
    max_lifetime_seconds: 0,
    material: []
};
const CredentialRefreshEditor = ({ refresh, onChange }) => {
    const update = (partial) => onChange({ ...refresh, ...partial });
    const updateMaterial = (i, m) => update({ material: (refresh.material ?? []).map((x, j) => (j === i ? m : x)) });
    const removeMaterial = (i) => update({ material: (refresh.material ?? []).filter((_, j) => j !== i) });
    const addMaterial = () => update({ material: [...(refresh.material ?? []), { name: "" }] });
    const handleStrategyUpdate = (strategy) => {
        const updatePayload = { strategy };
        if (strategy !== refresh.strategy && (refresh.material === undefined || refresh.material.length === 0)) {
            updatePayload.material = getDefaultMaterialForStrategy(strategy);
        }
        return update(updatePayload);
    };
    return (React.createElement("div", { style: { marginTop: 10, display: "flex", flexDirection: "column", gap: 8 } },
        React.createElement("div", { className: styles.fieldRow },
            React.createElement("div", { className: styles.field },
                React.createElement("div", { className: styles.labelWithInfo },
                    React.createElement("label", { className: styles.label }, "Strategy"),
                    React.createElement(InfoTip, { title: "Refresh Strategy" },
                        "How the credential is refreshed. ",
                        React.createElement("strong", null, "static"),
                        " means it never expires. ",
                        React.createElement("strong", null, "oauth2"),
                        " ",
                        "uses the token URL to exchange refresh material for a new token.")),
                React.createElement("select", { className: styles.select, value: refresh.strategy ?? "static", onChange: (e) => handleStrategyUpdate(e.target.value) },
                    React.createElement("option", { value: "static" }, "static"),
                    React.createElement("option", { value: "external" }, "external"),
                    React.createElement("option", { value: "oauth2_refresh_token" }, "oauth2_refresh_token"),
                    React.createElement("option", { value: "oauth2_client_credentials" }, "oauth2_client_credentials"),
                    React.createElement("option", { value: "google_service_account_jwt" }, "google_service_account_jwt"))),
            refresh.strategy !== "static" && refresh.strategy !== "external" && (React.createElement("div", { className: styles.field },
                React.createElement("div", { className: styles.labelWithInfo },
                    React.createElement("label", { className: styles.label }, "Token URL"),
                    React.createElement(InfoTip, { title: "Token URL" }, "The endpoint used to exchange refresh material for a new access token.")),
                React.createElement("input", { className: styles.inputSmall, value: refresh.token_url ?? "", onChange: (e) => update({ token_url: e.target.value || undefined }), placeholder: "https://auth.example.com/token" })))),
        refresh.strategy !== "static" && refresh.strategy !== "external" && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: styles.fieldRow },
                React.createElement("div", { className: styles.field },
                    React.createElement("div", { className: styles.labelWithInfo },
                        React.createElement("label", { className: styles.label }, "Refresh before (s)"),
                        React.createElement(InfoTip, { title: "Refresh Before Seconds" }, "How many seconds before expiry to proactively refresh the token. 0 means refresh only after expiry.")),
                    React.createElement("input", { className: styles.inputSmall, type: "number", min: 0, value: refresh.refresh_before_seconds ?? 0, onChange: (e) => update({ refresh_before_seconds: parseInt(e.target.value) || 0 }) })),
                React.createElement("div", { className: styles.field },
                    React.createElement("div", { className: styles.labelWithInfo },
                        React.createElement("label", { className: styles.label }, "Max lifetime (s)"),
                        React.createElement(InfoTip, { title: "Max Lifetime Seconds" }, "Maximum number of seconds the token is considered valid. 0 means no cap beyond what the server returns.")),
                    React.createElement("input", { className: styles.inputSmall, type: "number", min: 0, value: refresh.max_lifetime_seconds ?? 0, onChange: (e) => update({ max_lifetime_seconds: parseInt(e.target.value) || 0 }) }))),
            React.createElement("div", { className: styles.field },
                React.createElement("div", { className: styles.labelWithInfo },
                    React.createElement("span", { className: styles.subLabel }, "Scopes"),
                    React.createElement(InfoTip, { title: "Scopes" }, "OAuth2 scopes to request when refreshing the token.")),
                React.createElement("div", { className: styles.pathList },
                    (refresh.scopes ?? []).map((s, i) => (React.createElement("div", { key: i, className: styles.pathItem },
                        React.createElement("input", { className: styles.inputSmall, value: s, onChange: (e) => {
                                const next = [...(refresh.scopes ?? [])];
                                next[i] = e.target.value;
                                update({ scopes: next });
                            }, placeholder: "read:data" }),
                        React.createElement("button", { className: styles.removeBtn, onClick: () => update({ scopes: (refresh.scopes ?? []).filter((_, j) => j !== i) }) }, "\u00D7")))),
                    React.createElement("button", { className: styles.addBtn, onClick: () => update({ scopes: [...(refresh.scopes ?? []), ""] }) }, "+ Add scope"))),
            React.createElement("div", { className: styles.labelWithInfo, style: { marginTop: 8 } },
                React.createElement("span", { className: styles.subLabel }, "Material"),
                React.createElement(InfoTip, { title: "Refresh Material" }, "Named inputs the gateway needs to perform the refresh (e.g. a client ID and secret). Mark sensitive fields as Secret.")),
            (refresh.material ?? []).map((m, i) => (React.createElement(CredentialRefreshMaterialEditor, { key: i, material: m, onChange: (m) => updateMaterial(i, m), onRemove: () => removeMaterial(i) }))),
            React.createElement("button", { className: styles.addBtn, onClick: addMaterial }, "+ Add material")))));
};
// ── CredentialProfileEditor ────────────────────────────────────────────
const CredentialProfileEditor = ({ cred, onChange, onRemove }) => {
    const update = (partial) => onChange({ ...cred, ...partial });
    return (React.createElement("div", { className: styles.endpointCard },
        React.createElement("div", { className: styles.endpointHeader },
            React.createElement("span", { className: styles.endpointLabel }, cred.name || "new credential"),
            React.createElement("button", { className: styles.removeBtn, onClick: onRemove, title: "Remove" }, "\u00D7")),
        React.createElement("div", { className: styles.fieldRow },
            React.createElement("div", { className: styles.field },
                React.createElement("label", { className: styles.label }, "Name"),
                React.createElement("input", { className: styles.inputSmall, value: cred.name, onChange: (e) => update({ name: e.target.value }), placeholder: "my-credential" })),
            React.createElement("div", { className: styles.field },
                React.createElement("div", { className: styles.labelWithInfo },
                    React.createElement("label", { className: styles.label }, "Auth style"),
                    React.createElement(InfoTip, { title: "Auth Style" }, "How this credential is delivered to the upstream service.")),
                React.createElement("select", { className: styles.select, value: cred.auth_style ?? "", onChange: (e) => update({ auth_style: (e.target.value || undefined) }) },
                    React.createElement("option", { value: "" }, "Default"),
                    React.createElement("option", { value: "basic" }, "basic"),
                    React.createElement("option", { value: "bearer" }, "bearer"),
                    React.createElement("option", { value: "header" }, "header"),
                    React.createElement("option", { value: "query" }, "query")))),
        cred.auth_style === "header" && (React.createElement("div", { className: styles.field },
            React.createElement("label", { className: styles.label }, "Header name"),
            React.createElement("input", { className: styles.inputSmall, value: cred.header_name ?? "", onChange: (e) => update({ header_name: e.target.value || undefined }), placeholder: "Authorization" }))),
        cred.auth_style === "query" && (React.createElement("div", { className: styles.field },
            React.createElement("label", { className: styles.label }, "Query param"),
            React.createElement("input", { className: styles.inputSmall, value: cred.query_param ?? "", onChange: (e) => update({ query_param: e.target.value || undefined }), placeholder: "api_key" }))),
        React.createElement("div", { className: styles.field },
            React.createElement("label", { className: styles.label }, "Description"),
            React.createElement("input", { className: styles.inputSmall, value: cred.description ?? "", onChange: (e) => update({ description: e.target.value || undefined }), placeholder: "Optional description" })),
        React.createElement("div", { className: styles.field },
            React.createElement("div", { className: styles.labelWithInfo },
                React.createElement("span", { className: styles.subLabel }, "Env vars"),
                React.createElement(InfoTip, { title: "Environment Variables" }, "Environment variable names that carry this credential (e.g. OPENAI_API_KEY).")),
            React.createElement("div", { className: styles.pathList },
                (cred.env_vars ?? []).map((v, i) => (React.createElement("div", { key: i, className: styles.pathItem },
                    React.createElement("input", { className: styles.inputSmall, value: v, onChange: (e) => {
                            const next = [...(cred.env_vars ?? [])];
                            next[i] = e.target.value;
                            update({ env_vars: next });
                        }, placeholder: "MY_API_KEY" }),
                    React.createElement("button", { className: styles.removeBtn, onClick: () => update({ env_vars: (cred.env_vars ?? []).filter((_, j) => j !== i) }) }, "\u00D7")))),
                React.createElement("button", { className: styles.addBtn, onClick: () => update({ env_vars: [...(cred.env_vars ?? []), ""] }) }, "+ Add env var"))),
        React.createElement("label", { className: styles.checkbox },
            React.createElement("input", { type: "checkbox", checked: !!cred.required, onChange: (e) => update({ required: e.target.checked }) }),
            "Required"),
        React.createElement("div", { style: { marginTop: 10 } },
            React.createElement("div", { className: styles.labelWithInfo },
                React.createElement("span", { className: styles.subLabel }, "Token refresh"),
                React.createElement(InfoTip, { title: "Token Refresh" }, "Configure how the gateway refreshes this credential automatically. Enable to expand refresh settings.")),
            React.createElement("label", { className: styles.checkbox, style: { marginBottom: 6 } },
                React.createElement("input", { type: "checkbox", checked: cred.refresh !== undefined, onChange: (e) => update({ refresh: e.target.checked ? { ...DEFAULT_REFRESH } : undefined }) }),
                "Enable refresh"),
            cred.refresh !== undefined && (React.createElement(CredentialRefreshEditor, { refresh: cred.refresh, onChange: (r) => update({ refresh: r }) })))));
};
// ── ProfileForm ────────────────────────────────────────────────────────
const ProfileForm = ({ profile, mode, isLoading, errorMessage, onChange, onSubmit, onDelete, onCancel }) => {
    const update = (partial) => onChange({ ...profile, ...partial });
    const updateEndpoint = (i, ep) => update({ endpoints: (profile.endpoints ?? []).map((e, j) => (j === i ? ep : e)) });
    const removeEndpoint = (i) => update({ endpoints: (profile.endpoints ?? []).filter((_, j) => j !== i) });
    const addEndpoint = () => update({ endpoints: [...(profile.endpoints ?? []), { host: "", port: 443 }] });
    const updateCred = (i, c) => update({ credentials: (profile.credentials ?? []).map((x, j) => (j === i ? c : x)) });
    const removeCred = (i) => update({ credentials: (profile.credentials ?? []).filter((_, j) => j !== i) });
    const addCred = () => update({ credentials: [...(profile.credentials ?? []), { name: "" }] });
    const isValid = profile.id.trim().length > 0;
    return (React.createElement("div", { className: styles.policyEntryBody },
        React.createElement("div", { className: styles.fieldRow },
            React.createElement("div", { className: styles.field },
                React.createElement("div", { className: styles.labelWithInfo },
                    React.createElement("label", { className: styles.label }, "Profile ID"),
                    React.createElement(InfoTip, { title: "Profile ID" }, "Unique identifier for this provider profile. Cannot be changed after import.")),
                React.createElement("input", { className: styles.inputSmall + (mode === "update" ? " " + styles.disabled : ""), value: profile.id, onChange: (e) => update({ id: e.target.value }), disabled: mode === "update", placeholder: "my-provider" })),
            React.createElement("div", { className: styles.field },
                React.createElement("label", { className: styles.label }, "Display name"),
                React.createElement("input", { className: styles.inputSmall, value: profile.display_name ?? "", onChange: (e) => update({ display_name: e.target.value || undefined }), placeholder: "My Provider" }))),
        React.createElement("div", { className: styles.fieldRow },
            React.createElement("div", { className: styles.field },
                React.createElement("label", { className: styles.label }, "Category"),
                React.createElement("select", { className: styles.select, value: profile.category ?? "other", onChange: (e) => update({ category: e.target.value }) },
                    React.createElement("option", { value: "other" }, "other"),
                    React.createElement("option", { value: "inference" }, "inference"),
                    React.createElement("option", { value: "agent" }, "agent"),
                    React.createElement("option", { value: "source_control" }, "source_control"),
                    React.createElement("option", { value: "messaging" }, "messaging"),
                    React.createElement("option", { value: "data" }, "data"),
                    React.createElement("option", { value: "knowledge" }, "knowledge"))),
            React.createElement("div", { className: styles.field, style: { justifyContent: "center", paddingTop: 18 } },
                React.createElement("label", { className: styles.checkbox },
                    React.createElement("input", { type: "checkbox", checked: !!profile.inference_capable, onChange: (e) => update({ inference_capable: e.target.checked }) }),
                    "Inference capable"))),
        React.createElement("div", { className: styles.field },
            React.createElement("label", { className: styles.label }, "Description"),
            React.createElement("input", { className: styles.inputSmall, value: profile.description ?? "", onChange: (e) => update({ description: e.target.value || undefined }), placeholder: "Short description of this provider" })),
        React.createElement("div", { className: styles.labelWithInfo, style: { marginTop: 12 } },
            React.createElement("span", { className: styles.subLabel }, "Endpoints"),
            React.createElement(InfoTip, { title: "Endpoints" }, "External services this provider connects to, with protocol inspection and rule details.")),
        (profile.endpoints ?? []).map((ep, i) => (React.createElement(EndpointProfileEditor, { key: i, endpoint: ep, onChange: (ep) => updateEndpoint(i, ep), onRemove: () => removeEndpoint(i) }))),
        React.createElement("button", { className: styles.addBtn, onClick: addEndpoint }, "+ Add endpoint"),
        React.createElement("div", { className: styles.labelWithInfo, style: { marginTop: 12 } },
            React.createElement("span", { className: styles.subLabel }, "Binaries"),
            React.createElement(InfoTip, { title: "Allowed Binaries" }, "Programs permitted to make connections through this provider. Optionally mark a binary as a harness.")),
        React.createElement(BinaryProfileList, { binaries: profile.binaries ?? [], onChange: (binaries) => update({ binaries }) }),
        React.createElement("div", { className: styles.labelWithInfo, style: { marginTop: 12 } },
            React.createElement("span", { className: styles.subLabel }, "Credential Profiles"),
            React.createElement(InfoTip, { title: "Credential Profiles" }, "Credential slots this provider expects. Each entry describes how the credential is delivered to the sandbox (header, query param, env var, etc.).")),
        (profile.credentials ?? []).map((c, i) => (React.createElement(CredentialProfileEditor, { key: i, cred: c, onChange: (c) => updateCred(i, c), onRemove: () => removeCred(i) }))),
        React.createElement("button", { className: styles.addBtn, onClick: addCred }, "+ Add credential profile"),
        React.createElement("div", { className: styles.labelWithInfo, style: { marginTop: 12 } },
            React.createElement("span", { className: styles.subLabel }, "Discovery credentials"),
            React.createElement(InfoTip, { title: "Discovery" }, "Credential names used during provider auto-detection. Usually a subset of the credential profile names above.")),
        React.createElement("div", { className: styles.pathList },
            (profile.discovery?.credentials ?? []).map((c, i) => (React.createElement("div", { key: i, className: styles.pathItem },
                React.createElement("input", { className: styles.inputSmall, value: c, onChange: (e) => {
                        const next = [...(profile.discovery?.credentials ?? [])];
                        next[i] = e.target.value;
                        update({ discovery: { credentials: next } });
                    }, placeholder: "my-credential" }),
                React.createElement("button", { className: styles.removeBtn, onClick: () => update({
                        discovery: { credentials: (profile.discovery?.credentials ?? []).filter((_, j) => j !== i) }
                    }) }, "\u00D7")))),
            React.createElement("button", { className: styles.addBtn, onClick: () => update({ discovery: { credentials: [...(profile.discovery?.credentials ?? []), ""] } }) }, "+ Add discovery credential")),
        React.createElement("div", { style: { display: "flex", alignItems: "flex-start", marginTop: 14, gap: 8 } },
            React.createElement("button", { className: styles.addBtn + (isValid && !isLoading ? "" : " " + styles.disabled), onClick: onSubmit, disabled: !isValid || isLoading }, isLoading ? "Saving…" : mode === "create" ? "+ Import" : "+ Save"),
            onDelete && (React.createElement("button", { className: styles.removeBtn + (!isLoading ? "" : " " + styles.disabled), style: { width: "auto", height: "auto", lineHeight: "normal", padding: "5px 10px", fontSize: "11.5px" }, onClick: onDelete, disabled: isLoading }, "\u00D7 Delete")),
            onCancel && (React.createElement("button", { className: styles.removeBtn, style: { width: "auto", height: "auto", lineHeight: "normal", padding: "5px 10px", fontSize: "11.5px" }, onClick: onCancel }, "Cancel")),
            errorMessage && React.createElement("div", { className: styles.errorMessage }, errorMessage))));
};
// ── ProfileEntryCard ───────────────────────────────────────────────────
const ProfileEntryCard = ({ summary, detail, isLoading, onOpen, onSave, onDelete }) => {
    const [open, setOpen] = useState(false);
    const [localProfile, setLocalProfile] = useState();
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState();
    useEffect(() => {
        if (detail && !localProfile) {
            setLocalProfile(detail);
        }
    }, [detail]);
    const handleToggle = () => {
        if (!open)
            onOpen();
        setOpen((v) => !v);
    };
    const handleSave = () => {
        if (!localProfile)
            return;
        setIsSaving(true);
        setError(undefined);
        onSave(localProfile)
            .catch((e) => setError(e?.message ?? "Save failed"))
            .finally(() => setIsSaving(false));
    };
    const handleDelete = () => {
        setIsDeleting(true);
        setError(undefined);
        onDelete().catch((e) => {
            setError(e?.message ?? "Delete failed");
            setIsDeleting(false);
        });
    };
    return (React.createElement("div", { className: styles.policyEntry },
        React.createElement("button", { className: styles.policyEntryHeader, onClick: handleToggle },
            React.createElement("span", { className: styles.policyEntryName },
                React.createElement("span", { className: open ? styles.chevronOpen : styles.chevron }, "\u25B8"),
                summary.display_name || summary.id),
            React.createElement("span", { className: styles.policyEntryMeta },
                [summary.category, summary.inference_capable ? "inference" : undefined].filter(Boolean).join(" · "),
                isLoading && " loading…")),
        open && (React.createElement(React.Fragment, null, localProfile ? (React.createElement(ProfileForm, { profile: localProfile, mode: "update", isLoading: isSaving || isDeleting, errorMessage: error, onChange: setLocalProfile, onSubmit: handleSave, onDelete: handleDelete })) : (React.createElement("div", { className: styles.policyEntryBody, style: { color: "#9b95ae", fontSize: 12 } }, isLoading ? "Loading…" : "Failed to load profile details."))))));
};
// ── ProfileCreator ─────────────────────────────────────────────────────
const emptyProfile = () => ({
    id: "",
    display_name: "",
    description: "",
    category: "other",
    inference_capable: false,
    endpoints: [],
    binaries: [],
    credentials: [],
    discovery: { credentials: [] }
});
// ── TextProfileImporter ────────────────────────────────────────────────
const detectFormat = (input) => {
    try {
        JSON.parse(input);
        return "json";
    }
    catch {
        return "yaml";
    }
};
const TextProfileImporter = ({ onLint, onImport, onCancel }) => {
    const [text, setText] = useState("");
    const [lintResult, setLintResult] = useState();
    const [parseError, setParseError] = useState();
    const [isLinting, setIsLinting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const tryParse = (input) => {
        try {
            return yaml.parse(input);
        }
        catch {
            return null;
        }
    };
    const handleTextChange = (input) => {
        setText(input);
        setLintResult(undefined);
        if (input.trim() && !tryParse(input)) {
            setParseError("Invalid YAML / JSON");
        }
        else {
            setParseError(undefined);
        }
    };
    const parsed = text.trim() ? tryParse(text) : null;
    const busy = isLinting || isImporting;
    const lintRequest = () => ({ content: text, format: detectFormat(text) });
    const handleValidate = () => {
        if (!parsed)
            return;
        setIsLinting(true);
        setLintResult(undefined);
        onLint(lintRequest())
            .then(setLintResult)
            .finally(() => setIsLinting(false));
    };
    const handleImport = () => {
        if (!parsed)
            return;
        setIsImporting(true);
        setLintResult(undefined);
        onLint(lintRequest())
            .then((result) => {
            setLintResult(result);
            if (!result.valid)
                return;
            return onImport(parsed);
        })
            .catch((e) => setLintResult({ valid: false, message: e?.message ?? "Import failed" }))
            .finally(() => setIsImporting(false));
    };
    return (React.createElement("div", { className: styles.policyEntryBody },
        React.createElement("div", { className: styles.field },
            React.createElement("div", { className: styles.labelWithInfo },
                React.createElement("span", { className: styles.subLabel }, "YAML or JSON"),
                React.createElement(InfoTip, { title: "YAML / JSON Input" }, "Paste a provider profile in YAML or JSON format. The profile will be validated before import.")),
            React.createElement("textarea", { className: styles.jsonEditor, style: { minHeight: 220 }, value: text, onChange: (e) => handleTextChange(e.target.value), placeholder: "id: my-provider\ndisplay_name: My Provider\n...", spellCheck: false }),
            parseError && React.createElement("div", { className: styles.jsonError }, parseError),
            lintResult && (React.createElement("div", { className: lintResult.valid ? undefined : styles.errorMessage, style: lintResult.valid ? { color: "#16a34a", fontSize: 12, marginTop: 4 } : undefined },
                React.createElement(MarkdownPanel, { overrides: { p: { style: {} } } }, lintResult.valid ? `✓ ${lintResult.message || "Valid"}` : `${lintResult.message}`)))),
        React.createElement("div", { style: { display: "flex", gap: 8, marginTop: 8 } },
            React.createElement("button", { className: styles.addBtn + (!parsed || busy ? " " + styles.disabled : ""), onClick: handleValidate, disabled: !parsed || busy }, isLinting ? "Validating…" : "Validate"),
            React.createElement("button", { className: styles.addBtn + (!parsed || busy ? " " + styles.disabled : ""), onClick: handleImport, disabled: !parsed || busy }, isImporting ? "Importing…" : "+ Import"),
            React.createElement("button", { className: styles.removeBtn, style: { width: "auto", height: "auto", lineHeight: "normal", padding: "5px 10px", fontSize: "11.5px" }, onClick: onCancel }, "Cancel"))));
};
const ProfileCreator = ({ onImport, onLint }) => {
    const [open, setOpen] = useState(false);
    const [inputMode, setInputMode] = useState("form");
    const [profile, setProfile] = useState(emptyProfile());
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState();
    const handleClose = () => {
        setOpen(false);
        setProfile(emptyProfile());
        setError(undefined);
    };
    const handleFormSubmit = () => {
        setIsLoading(true);
        setError(undefined);
        onImport(profile)
            .then(handleClose)
            .catch((e) => setError(e?.message ?? "Import failed"))
            .finally(() => setIsLoading(false));
    };
    if (!open) {
        return (React.createElement("button", { className: styles.addBtn, onClick: () => setOpen(true) }, "+ New profile"));
    }
    return (React.createElement("div", { className: styles.policyEntry },
        React.createElement("div", { className: styles.policyEntryHeader, style: { cursor: "default" } },
            React.createElement("span", { className: styles.policyEntryName }, "New provider profile"),
            React.createElement("div", { className: styles.modeToggle },
                React.createElement("button", { className: inputMode === "form" ? styles.modeBtnActive : styles.modeBtn, onClick: () => setInputMode("form") }, "Form"),
                React.createElement("button", { className: inputMode === "text" ? styles.modeBtnActive : styles.modeBtn, onClick: () => setInputMode("text") }, "YAML \u00B7 JSON"))),
        inputMode === "text" ? (React.createElement(TextProfileImporter, { onLint: onLint, onImport: onImport, onCancel: handleClose })) : (React.createElement(ProfileForm, { profile: profile, mode: "create", isLoading: isLoading, errorMessage: error, onChange: setProfile, onSubmit: handleFormSubmit, onCancel: handleClose }))));
};
// ── Main ProviderProfileEditor ─────────────────────────────────────────
const ProviderProfileEditor = ({ openShellClient }) => {
    const [profiles, setProfiles] = useState([]);
    const [details, setDetails] = useState({});
    const [loadingIds, setLoadingIds] = useState({});
    const [listError, setListError] = useState();
    const refresh = () => {
        openShellClient
            .getProviderProfiles()
            .then(setProfiles)
            .catch(() => setListError("Failed to load provider profiles"));
    };
    useEffect(() => {
        refresh();
    }, []);
    const loadDetail = (id) => {
        if (details[id] !== undefined)
            return;
        setLoadingIds((prev) => ({ ...prev, [id]: true }));
        openShellClient
            .getProviderProfile(id)
            .then((detail) => setDetails((prev) => ({ ...prev, [id]: detail })))
            .finally(() => setLoadingIds((prev) => ({ ...prev, [id]: false })));
    };
    const handleSave = (id) => (updated) => openShellClient.createProviderProfile(updated).then((saved) => {
        setDetails((prev) => ({ ...prev, [id]: saved }));
        setProfiles((prev) => prev.map((p) => (p.id === id ? saved : p)));
    });
    const handleDelete = (id) => () => openShellClient.deleteProviderProfile(id).then(() => {
        setProfiles((prev) => prev.filter((p) => p.id !== id));
        setDetails((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    });
    const handleImport = (profile) => openShellClient.createProviderProfile(profile).then((created) => {
        setProfiles((prev) => {
            const exists = prev.find((p) => p.id === created.id);
            return exists ? prev.map((p) => (p.id === created.id ? created : p)) : [...prev, created];
        });
        setDetails((prev) => ({ ...prev, [created.id]: created }));
    });
    return (React.createElement(React.Fragment, null,
        listError && React.createElement("div", { className: styles.errorMessage }, listError),
        profiles.map((summary) => (React.createElement(ProfileEntryCard, { key: summary.id, summary: summary, detail: details[summary.id], isLoading: !!loadingIds[summary.id], onOpen: () => loadDetail(summary.id), onSave: handleSave(summary.id), onDelete: handleDelete(summary.id) }))),
        React.createElement(ProfileCreator, { onImport: handleImport, onLint: (p) => openShellClient.lintProviderProfile(p) })));
};
export default ProviderProfileEditor;
